import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import {
  TableOptions,
  SortingState,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnFiltersState,
  ColumnOrderState,
  VisibilityState,
  OnChangeFn,
  ColumnPinningState,
} from '@tanstack/react-table';
import { CrudFilter } from '@sib/types';
import { DEFAULT_MIN_PAGE, getRowId, isRowWithId } from '../utils';
import { getTableStore } from './use-table-store';

export const PAGE_SIZES = [5, 10, 20, 50, 100];

interface UseTableSettingsProps<T> {
  tableId: string;
  sorting?: boolean;
  clientSorting?: boolean;
  clientPagination?: boolean;
  minPage?: number;
  defaultPageSize?: number;
  defaultSortingState?: SortingState;
  onOriginalRowSelectionChange?: (data: T[]) => void;
  columnVisibility?: VisibilityState | null;
  columnPinning?: ColumnPinningState | null;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  defaultColumnPinningState?: ColumnPinningState;
  enableColumnReordering?: boolean;
  defaultColumnOrder?: ColumnOrderState;
  disabledColumns?: Set<string>;
  exportAction?: () => void;
  isExportPending?: boolean;
  uploadAction?: () => void;
}

export const pageSizeOptions = PAGE_SIZES.map(size => ({
  text: size,
  value: String(size),
}));

export const useTableSettings = <T>({
  tableId,
  sorting: withSorting = true,
  clientSorting = false,
  clientPagination = false,
  minPage = DEFAULT_MIN_PAGE,
  defaultColumnPinningState = { left: [], right: [] },
  defaultPageSize = 10,
  defaultSortingState = [],
  columnVisibility,
  columnPinning: preferencesColumnPinning,
  onColumnVisibilityChange,
  onColumnPinningChange,
  enableColumnReordering = false,
  defaultColumnOrder = [],
  disabledColumns = new Set(),
  exportAction,
  isExportPending,
  uploadAction,
}: UseTableSettingsProps<T>) => {
  const useTableStore = getTableStore(tableId);

  const {
    initialize,
    initialized,
    sorting,
    setSorting,
    columnPinning,
    columnFilters,
    setColumnFilters,
    columnOrder,
    setColumnOrder,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
    viewFilters,
    setViewFilters,
  } = useTableStore();

  const appliedSorting = useMemo(
    () => (initialized ? sorting : defaultSortingState),
    [initialized, sorting, defaultSortingState]
  );

  const appliedPagination = useMemo(
    () =>
      initialized
        ? pagination
        : { pageIndex: minPage, pageSize: defaultPageSize },
    [initialized, pagination, minPage, defaultPageSize]
  );

  const [filters, setFilters] = useState<CrudFilter[]>([]);
  const previousFiltersRef = useRef<CrudFilter[]>([]);

  const [originalRowSelection, setOriginalRowSelection] = useState<T[]>([]);

  const selectedRowIds = useMemo(
    () => originalRowSelection.filter(isRowWithId).map(row => row.id),
    [originalRowSelection]
  );

  const clearRowSelection = useCallback(() => {
    setRowSelection({});
    setOriginalRowSelection([]);
  }, [setRowSelection]);

  const hasFiltersSet = filters.length > 0 || columnFilters.length > 0;

  const setFiltersWithComparison = useCallback(
    (newFilters: CrudFilter[]) => {
      const areEqual =
        JSON.stringify(newFilters) ===
        JSON.stringify(previousFiltersRef.current);

      if (!areEqual) {
        previousFiltersRef.current = newFilters;
        setFilters(newFilters);
        setPagination(p => ({ ...p, pageIndex: minPage }));
      }
    },
    [minPage, setPagination]
  );

  const onColumnFiltersChangeWithReset: OnChangeFn<ColumnFiltersState> =
    useCallback(
      updater => {
        setColumnFilters(prev =>
          typeof updater === 'function' ? updater(prev) : updater
        );
        setPagination(p => ({ ...p, pageIndex: minPage }));
      },
      [minPage, setPagination, setColumnFilters]
    );

  const onSortingChangeWithReset: OnChangeFn<SortingState> = useCallback(
    updater => {
      setSorting(prev =>
        typeof updater === 'function' ? updater(prev) : updater
      );
      setPagination(p => ({ ...p, pageIndex: minPage }));
    },
    [minPage, setPagination, setSorting]
  );

  const appliedColumnOrder = useMemo(
    () => (initialized ? columnOrder : defaultColumnOrder),
    [initialized, columnOrder, defaultColumnOrder]
  );

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    updater => {
      setColumnOrder(prev =>
        typeof updater === 'function' ? updater(prev) : updater
      );
    },
    [setColumnOrder]
  );

  const mergedColumnPinning = useMemo(() => {
    const preferences =
      preferencesColumnPinning &&
      Array.isArray(preferencesColumnPinning.left) &&
      Array.isArray(preferencesColumnPinning.right)
        ? preferencesColumnPinning
        : null;

    const defaultLeft = defaultColumnPinningState.left ?? [];
    const defaultRight = defaultColumnPinningState.right ?? [];
    const defaultLeftSet = new Set(defaultLeft);
    const defaultRightSet = new Set(defaultRight);

    const uniqueIdsExcludingDefaults = (
      target: string[],
      ids: string[] | undefined,
      defaultSet: Set<string>
    ) =>
      ids?.forEach(id => {
        if (!defaultSet.has(id) && !target.includes(id)) target.push(id);
      });

    const leftRest: string[] = [];
    const rightRest: string[] = [];

    uniqueIdsExcludingDefaults(leftRest, columnPinning.left, defaultLeftSet);
    uniqueIdsExcludingDefaults(leftRest, preferences?.left, defaultLeftSet);

    uniqueIdsExcludingDefaults(rightRest, columnPinning.right, defaultRightSet);
    uniqueIdsExcludingDefaults(rightRest, preferences?.right, defaultRightSet);

    return {
      left: [...defaultLeft, ...leftRest],
      right: [...rightRest, ...defaultRight],
    };
  }, [columnPinning, preferencesColumnPinning, defaultColumnPinningState]);

  const tableProps: Partial<TableOptions<T>> = useMemo(
    () => ({
      manualPagination: !clientPagination,
      onPaginationChange: pagination => {
        setPagination(pagination);
      },
      enableSorting: withSorting,
      manualSorting: withSorting && !clientSorting,
      state: {
        pagination: appliedPagination,
        ...(withSorting && { sorting: appliedSorting }),
        rowSelection,
        columnFilters,
        originalRowSelection,
        ...(columnVisibility && { columnVisibility }),
        columnPinning: mergedColumnPinning,
        ...(enableColumnReordering && { columnOrder: appliedColumnOrder }),
      },
      getRowId,
      onRowSelectionChange: setRowSelection,
      ...(onColumnPinningChange && { onColumnPinningChange }),
      onColumnFiltersChange: onColumnFiltersChangeWithReset,
      ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
      onColumnOrderChange: enableColumnReordering
        ? onColumnOrderChange
        : undefined,
      ...(clientPagination && {
        getPaginationRowModel: getPaginationRowModel<T>(),
      }),
      ...(withSorting && {
        onSortingChange: onSortingChangeWithReset,
        ...(clientSorting && { getSortedRowModel: getSortedRowModel<T>() }),
      }),
    }),
    [
      clientPagination,
      withSorting,
      clientSorting,
      appliedPagination,
      appliedSorting,
      rowSelection,
      columnFilters,
      mergedColumnPinning,
      originalRowSelection,
      columnVisibility,
      enableColumnReordering,
      appliedColumnOrder,
      onColumnOrderChange,
      setRowSelection,
      onColumnPinningChange,
      onColumnFiltersChangeWithReset,
      onColumnVisibilityChange,
      onSortingChangeWithReset,
      setPagination,
    ]
  );

  const onLastElementDelete = useCallback(() => {
    if (appliedPagination.pageIndex > 0)
      setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }));
  }, [appliedPagination.pageIndex, setPagination]);

  const resetPagination = useCallback(
    () => setPagination(p => ({ ...p, pageIndex: minPage })),
    [minPage, setPagination]
  );

  const resetColumnFilters = useCallback(
    () => setColumnFilters([]),
    [setColumnFilters]
  );

  const resetViewFilters = useCallback(
    () => setViewFilters({}),
    [setViewFilters]
  );

  // const previousResetPaginationKeyRef = useRef(resetPaginationKey);
  // useEffect(() => {
  //   if (previousResetPaginationKeyRef.current !== resetPaginationKey) {
  //     previousResetPaginationKeyRef.current = resetPaginationKey;
  //     resetPagination();
  //   }
  // }, [resetPaginationKey, resetPagination]);

  useEffect(() => {
    if (initialized) return;

    initialize?.({
      sorting: defaultSortingState,
      pagination: {
        pageIndex: minPage,
        pageSize: defaultPageSize,
      },
      columnPinning: defaultColumnPinningState,
      columnOrder: defaultColumnOrder,
    });
  }, [
    defaultPageSize,
    minPage,
    initialize,
    initialized,
    defaultSortingState,
    defaultColumnPinningState,
    defaultColumnOrder,
  ]);

  return {
    sorting: appliedSorting,
    tableProps,
    pageSizeOptions,
    pagination: appliedPagination,
    minPage,
    filters,
    columnFilters,
    rowSelection,
    setRowSelection,
    hasFiltersSet,
    onLastElementDelete,
    defaultColumnPinningState,
    disabledColumns,
    enableColumnReordering,
    columnOrder: appliedColumnOrder,
    onColumnOrderChange,
    onFiltersChange: setFiltersWithComparison,
    onColumnFiltersChange: onColumnFiltersChangeWithReset,
    onPaginationChange: setPagination,
    onSortingChange: onSortingChangeWithReset,
    originalRowSelection,
    onOriginalRowSelectionChange: setOriginalRowSelection,
    selectedRowIds,
    clearRowSelection,
    resetPagination,
    resetColumnFilters,
    viewFilters,
    setViewFilters,
    resetViewFilters,
    initialized,
    exportAction,
    isExportPending,
    uploadAction,
  };
};
